import { useState } from 'react';
import { GoogleAuthProvider, signInWithPopup, type AuthCredential } from 'firebase/auth';

import { auth } from '../firebase';
import { ensureUserDocuments } from '../lib/account';
import {
  collidingEmail,
  googleCredentialFromError,
  linkGoogleToPasswordAccount,
} from '../lib/authLink';
import { errorCode } from '../lib/errors';
import Field from './Field';

/** Set when Google collided with an existing password account, carrying what the link needs. */
interface PendingLink {
  email: string;
  credential: AuthCredential;
}

/**
 * Continue with Google, on both signed-out screens.
 *
 * One component rather than a copy per screen, because the interesting part is
 * not the button — it is that Google sign-in has no separate "signup". The same
 * tap creates the account or returns to it, and only `ensureUserDocuments` can
 * tell which happened.
 *
 * It also owns the account-linking prompt. Somebody who has been signing in with
 * a password and now wants Google will simply tap this button, so this is where
 * that has to be answered — not in a settings screen they have no reason to
 * visit, and certainly not with a raw Firebase error code.
 */
function GoogleButton({ onDone, onError }: { onDone: () => void; onError: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingLink | null>(null);

  const handleClick = async () => {
    setBusy(true);
    onError('');

    try {
      const provider = new GoogleAuthProvider();
      // Always ask which account. Without this, anyone signed into exactly one
      // Google account is silently signed in as it, which is the wrong default
      // on a shared or family machine.
      provider.setCustomParameters({ prompt: 'select_account' });

      const { user } = await signInWithPopup(auth, provider);
      await ensureUserDocuments(user, user.displayName ?? undefined);

      onDone();
    } catch (err) {
      const code = errorCode(err);

      // Backing out of the picker is not a failure and must not paint an error.
      // `cancelled-popup-request` fires on a double tap, where the first popup
      // is discarded — also not something to report.
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return;
      }

      if (code === 'auth/account-exists-with-different-credential') {
        const credential = googleCredentialFromError(err);
        const email = collidingEmail(err);

        // Both are needed to finish: the credential cannot be re-obtained
        // without another popup, and the email is what we sign in with.
        if (credential && email) {
          setPending({ email, credential });
          return;
        }

        onError('That email already has a password here. Sign in with your password instead.');
        return;
      }

      console.error('[GoogleButton] sign in failed:', err);

      if (code === 'auth/popup-blocked') {
        onError('Your browser blocked the sign-in window. Allow popups for this site, then try again.');
      } else if (code === 'auth/network-request-failed') {
        onError('Could not reach Google. Check your connection and try again.');
      } else {
        onError('Could not sign in with Google. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <LinkPrompt
        pending={pending}
        onCancel={() => setPending(null)}
        onDone={onDone}
        onError={onError}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className="press flex w-full items-center justify-center gap-3 rounded-xl border-2 border-ink bg-white px-5 py-3 font-display text-lg font-bold shadow-pop disabled:cursor-not-allowed disabled:text-ink/30"
    >
      <GoogleMark />
      {busy ? 'Opening Google...' : 'Continue with Google'}
    </button>
  );
}

/**
 * "You already have a password. Type it once and we will connect Google."
 *
 * Asking for the password is not a formality. Linking on a matching email
 * address alone would mean anyone able to create a Google account carrying your
 * address could take over your puns, your crowns and your follows. The password
 * is the proof that the two accounts are the same person.
 */
function LinkPrompt({
  pending,
  onCancel,
  onDone,
  onError,
}: {
  pending: PendingLink;
  onCancel: () => void;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);

    try {
      const user = await linkGoogleToPasswordAccount(pending.email, password, pending.credential);
      await ensureUserDocuments(user);
      onDone();
    } catch (err) {
      console.error('[GoogleButton] could not link Google:', err);
      const code = errorCode(err);

      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setProblem('That password does not match. Try again.');
      } else if (code === 'auth/too-many-requests') {
        setProblem('Too many attempts. Wait a few minutes and try again.');
      } else if (code === 'auth/credential-already-in-use') {
        // The Google account is attached to a *different* Pun and Done account.
        setProblem('That Google account is already connected to another account here.');
      } else {
        setProblem('Could not connect Google. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-xl border-2 border-ink bg-lagoon/10 px-5 py-4 shadow-pop-sm"
    >
      <p className="font-display font-bold">One quick thing.</p>
      <p className="mb-4 mt-1 text-sm text-ink/65">
        <span className="font-semibold">{pending.email}</span> already has a password here.
        Enter it once and Google will be connected to the same account — after this, either
        one signs you in.
      </p>

      {problem && (
        <p className="mb-3 rounded-lg border-2 border-ink bg-coral px-3 py-2 text-sm font-medium text-white">
          {problem}
        </p>
      )}

      <Field
        label="Your password"
        type="password"
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
        required
      />

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={busy || !password}
          className="press flex-1 rounded-xl border-2 border-ink bg-mustard px-4 py-2.5 font-display font-bold shadow-pop-sm disabled:cursor-not-allowed disabled:bg-ink/10 disabled:text-ink/30"
        >
          {busy ? 'Connecting...' : 'Connect Google'}
        </button>
        <button
          type="button"
          onClick={() => {
            onError('');
            onCancel();
          }}
          className="press rounded-xl border-2 border-ink bg-white px-4 py-2.5 font-display font-bold shadow-pop-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Google's mark, inline. Their brand guidelines want it unmodified and on white. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5 shrink-0" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

export default GoogleButton;
